import type { User, UserRole } from '@backend/prisma';
import type { CreditHistoryItem, EmbeddedItem, QuotasData } from '@stw/types';

import { stwFetch } from '@/utils/stwFetch';

export interface UserResult extends User {
  user_role: UserRole;
}

export interface PublicUserResult {
  id: string;
  username: string;
  created_at: Date;
  avatar: string | null;
}

/**
 * Get current authenticated user's full profile data
 */
export const getUser = async (): Promise<UserResult> => {
  try {
    const { user } = await stwFetch('GET', '/user');
    return user;
  } catch (error: any) {
    if (error.status === 401) {
      throw new Error('Email not verified');
    }
    throw error;
  }
};

/**
 * Get public user information by ID (no authentication required)
 */
export const getPublicUser = async (
  id: User['id']
): Promise<PublicUserResult> => {
  const { user } = await stwFetch('GET', `/user/${id}`);
  return user;
};

/**
 * Get current user's quotas and usage information
 * @returns User quotas and credits data
 */
export const getQuotas = async (): Promise<QuotasData> => {
  const { quotas } = await stwFetch('GET', '/user/quotas');
  return quotas;
};

/**
 * Get paginated list of embedded items and scenes
 * @param page - Page number (1-indexed)
 * @param limit - Number of items per page
 * @returns Paginated embedded items and scenes
 */
export const getCreditUsageEmbed = async (
  page: number = 1,
  limit: number = 20
): Promise<{
  items: EmbeddedItem[];
  total: number;
  page: number;
  limit: number;
}> => {
  const data = await stwFetch(
    'GET',
    `/user/credit-usage/embed?page=${page}&limit=${limit}`
  );
  return data;
};

/**
 * Get paginated scan credit history
 * @param page - Page number (1-indexed)
 * @param limit - Number of items per page
 * @returns Paginated scan credit history
 */
export const getScanCreditHistory = async (
  page: number = 1,
  limit: number = 50
): Promise<{
  items: CreditHistoryItem[];
  total: number;
  page: number;
  limit: number;
}> => {
  const data = await stwFetch(
    'GET',
    `/user/credit-usage/scan-history?page=${page}&limit=${limit}`
  );
  return data;
};

/**
 * Get signed URL for avatar upload
 * @param filename - The filename including extension
 * @returns Upload URL and constraints
 */
export const getAvatarUploadUrl = async (
  filename: string
): Promise<{
  path: string;
  url: string;
  maxSize: number;
  allowedTypes: string[];
}> => {
  const data = await stwFetch(
    'GET',
    `/upload/avatar?filename=${encodeURIComponent(filename)}`
  );
  return data;
};

/**
 * Update user avatar path after successful upload
 * @param path - The storage path of the uploaded avatar
 * @returns The signed URL for the new avatar
 */
export const updateUserAvatar = async (path: string): Promise<string> => {
  const data = await stwFetch('PUT', '/user/avatar', { path });
  return data.avatar;
};

/**
 * Upload avatar file and update user profile
 * @param file - The image file to upload
 * @returns The signed URL for the new avatar
 */
export const uploadAvatar = async (file: File): Promise<string> => {
  // Get signed upload URL
  const { path, url } = await getAvatarUploadUrl(file.name);

  // Upload to signed URL
  if (import.meta.env.DEV) {
    await fetch(url, {
      method: 'POST',
      headers: { Authorization: 'Firebase owner' },
      body: file,
    });
  } else {
    await fetch(url, {
      method: 'PUT',
      body: file,
    });
  }

  // Update user avatar path in database and get signed URL
  const avatarUrl = await updateUserAvatar(path);
  return avatarUrl;
};
